import { useEffect, useState } from "react";
import { Section } from "@/components/CalcUI";
import { fetchMyTeam, joinTeam, leaveTeam, renameTeam, fetchTeamJobs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Users, Copy, LogOut, Lock, FileDown, Upload, Image as ImageIcon, Trash2, Camera } from "lucide-react";
import { JobPhotosDialog } from "@/components/JobPhotos";
import { toast } from "sonner";
import { downloadJobPdf, uploadTeamLogo, deleteTeamLogo, teamLogoUrl } from "@/lib/api";

export default function TeamPage() {
  const { user, isAuthed, hasPro, setShowAuth, refresh } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [photosJob, setPhotosJob] = useState(null);

  const load = async () => {
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    try {
      const t = await fetchMyTeam();
      setTeam(t);
      // If a team was auto-provisioned, refresh user so team_role is current
      if (t && (!user?.team_id || user?.team_id !== t.id)) {
        await refresh();
      }
      if (t) {
        try {
          const j = await fetchTeamJobs();
          setJobs(j);
        } catch {
          setJobs([]);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  if (!isAuthed) {
    return (
      <div data-testid="team-page">
        <Section title="Team Library">
          <div className="border border-[#334155] bg-[#11151c] p-8 text-center">
            <Lock className="w-8 h-8 text-[#f59e0b] mx-auto mb-3" />
            <p className="mono text-sm text-[#94a3b8] mb-4">Sign in to view your team.</p>
            <button
              data-testid="team-signin-btn"
              onClick={() => setShowAuth(true)}
              className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold"
            >
              Sign in
            </button>
          </div>
        </Section>
      </div>
    );
  }

  const isTeamPlan = user?.plan === "team" || user?.role === "admin";

  return (
    <div data-testid="team-page">
      <Section title="Team Library" subtitle="Share saved jobs with your crew. Only Team-plan owners can start a team; anyone with a join code can join.">
        {loading ? (
          <div className="mono text-sm text-[#94a3b8]">Loading…</div>
        ) : team ? (
          <>
            <div className="border border-[#334155] bg-[#11151c] p-5 mb-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs mono tracking-widest text-[#f59e0b] mb-1">TEAM</div>
                  <h3 className="heading text-xl font-bold" data-testid="team-name">{team.name}</h3>
                  <div className="text-xs mono text-[#94a3b8] mt-1">
                    {team.member_ids?.length} member{team.member_ids?.length > 1 ? "s" : ""} · role: {user?.team_role || "member"}
                  </div>
                </div>
                <div>
                  <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-1">Join Code</div>
                  <div className="flex items-center gap-2 border border-[#334155] px-3 py-2 bg-[#0b0e13]">
                    <span className="mono text-[#f59e0b] font-bold tracking-widest" data-testid="join-code">{team.join_code}</span>
                    <button
                      data-testid="copy-join-btn"
                      onClick={() => { navigator.clipboard.writeText(team.join_code); toast.success("Copied"); }}
                      className="text-[#94a3b8] hover:text-[#f59e0b]"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {user?.team_role === "owner" && (
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  <input
                    data-testid="team-rename-input"
                    placeholder={team.name}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] focus:outline-none px-3 py-2 mono text-sm"
                  />
                  <button
                    data-testid="team-rename-btn"
                    onClick={async () => {
                      if (!newName.trim()) return;
                      try {
                        await renameTeam(newName.trim());
                        toast.success("Team renamed");
                        load();
                        setNewName("");
                      } catch {
                        toast.error("Rename failed");
                      }
                    }}
                    className="border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-3 py-2 mono text-xs uppercase tracking-widest"
                  >
                    Rename
                  </button>
                </div>
              )}

              {user?.team_role === "owner" && (
                <div className="mt-5 pt-5 border-t border-[#334155]">
                  <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-2 flex items-center gap-2">
                    <ImageIcon className="w-3.5 h-3.5" /> Company Logo (PDF reports)
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {team.logo_path ? (
                      <img
                        data-testid="team-logo-preview"
                        src={`${teamLogoUrl()}?t=${team.logo_path}`}
                        alt="Team logo"
                        className="h-12 max-w-[160px] object-contain bg-white p-1 border border-[#334155]"
                      />
                    ) : (
                      <div className="h-12 w-32 border border-dashed border-[#334155] flex items-center justify-center text-[10px] mono text-[#64748b]">
                        NO LOGO
                      </div>
                    )}
                    <label className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-3 py-2 mono text-xs uppercase tracking-widest cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Upload
                      <input
                        data-testid="team-logo-input"
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 500 * 1024) {
                            toast.error("Logo must be under 500 KB");
                            return;
                          }
                          try {
                            await uploadTeamLogo(f);
                            toast.success("Logo uploaded");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Upload failed");
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {team.logo_path && (
                      <button
                        data-testid="team-logo-delete"
                        onClick={async () => {
                          if (!window.confirm("Remove logo?")) return;
                          await deleteTeamLogo();
                          toast.success("Logo removed");
                          load();
                        }}
                        className="text-[#94a3b8] hover:text-[#ef4444]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] mono text-[#64748b] mt-2">
                    PNG or JPEG · Max 500 KB · appears on every exported field report
                  </div>
                </div>
              )}

              <div className="mt-5">
                <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-2">Members</div>
                <ul className="space-y-1">
                  {team.members?.map((m) => (
                    <li key={m.id} className="mono text-sm text-[#f8fafc] flex justify-between items-center">
                      <span>{m.email} {m.name && <span className="text-[#94a3b8]">· {m.name}</span>}</span>
                      <span className="text-[10px] text-[#64748b]">{m.plan}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {user?.team_role !== "owner" && (
                <button
                  data-testid="leave-team-btn"
                  onClick={async () => {
                    if (!window.confirm("Leave team?")) return;
                    await leaveTeam();
                    toast.success("Left team");
                    load();
                  }}
                  className="mt-5 inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#ef4444] hover:text-[#ef4444] px-3 py-2 mono text-xs uppercase tracking-widest"
                >
                  <LogOut className="w-3.5 h-3.5" /> Leave Team
                </button>
              )}
            </div>

            <div className="mb-2 text-xs mono tracking-widest text-[#f59e0b]">SHARED JOBS · {jobs.length}</div>
            {jobs.length === 0 ? (
              <div className="border border-[#334155] p-8 text-center mono text-sm text-[#94a3b8]">
                No jobs shared yet. Owners: save a calculation and toggle Share.
              </div>
            ) : (
              <div className="border border-[#334155]">
                <table className="w-full mono text-sm">
                  <thead>
                    <tr className="border-b border-[#334155] bg-[#0b0e13] text-xs uppercase tracking-widest text-[#94a3b8]">
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Author</th>
                      <th className="text-left px-4 py-2">Category</th>
                      <th className="text-left px-4 py-2">Saved</th>
                      <th className="text-right px-4 py-2 w-16">Photos</th>
                      <th className="text-right px-4 py-2 w-16">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j, i) => (
                      <tr key={j.id} className={i % 2 === 0 ? "bg-[#0b0e13]" : "bg-[#11151c]"}>
                        <td className="px-4 py-2 text-[#f8fafc]">{j.name}</td>
                        <td className="px-4 py-2 text-[#94a3b8]">{j.author_email}</td>
                        <td className="px-4 py-2 text-[#94a3b8]">{j.category}</td>
                        <td className="px-4 py-2 text-[#94a3b8] text-xs">{new Date(j.created_at).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">
                          {j.photos?.length > 0 ? (
                            <button
                              data-testid={`team-photos-${j.id}`}
                              onClick={() => setPhotosJob(j)}
                              className="inline-flex items-center gap-1 text-[#94a3b8] hover:text-[#f59e0b]"
                            >
                              <Camera className="w-4 h-4" /> {j.photos.length}
                            </button>
                          ) : (
                            <span className="text-[#64748b]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            data-testid={`team-pdf-${j.id}`}
                            onClick={() => downloadJobPdf(j.id, `${j.name}.pdf`)}
                            className="text-[#94a3b8] hover:text-[#f59e0b]"
                          >
                            <FileDown className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : isTeamPlan ? (
          <div className="border border-[#334155] p-8 text-center">
            <Users className="w-8 h-8 text-[#f59e0b] mx-auto mb-3" />
            <p className="text-sm text-[#94a3b8] mb-4">Provisioning your team...</p>
            <button
              data-testid="team-refresh-btn"
              onClick={load}
              className="border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-3 py-2 mono text-xs uppercase tracking-widest"
            >
              Refresh
            </button>
          </div>
        ) : (
          <div className="border border-[#334155] bg-[#11151c] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-sm font-bold uppercase tracking-wide">Not on a team yet</h3>
            </div>
            <p className="text-sm text-[#94a3b8] mb-4">
              Join a team using a 8-character code from your crew owner, or upgrade to Team to start your own.
            </p>
            <div className="flex items-center gap-2">
              <input
                data-testid="join-code-input"
                placeholder="ENTER 8-CHAR CODE"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] focus:outline-none px-3 py-2 mono text-sm uppercase flex-1"
              />
              <button
                data-testid="join-team-btn"
                onClick={async () => {
                  if (!joinCode.trim()) return;
                  try {
                    await joinTeam(joinCode.trim());
                    toast.success("Joined team");
                    setJoinCode("");
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Invalid code");
                  }
                }}
                className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold"
              >
                Join
              </button>
            </div>
            <div className="mt-6 pt-6 border-t border-[#334155] text-center">
              <button
                data-testid="upgrade-team-btn"
                onClick={() => navigate("/pricing")}
                className="border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-4 py-2 mono text-xs tracking-widest uppercase"
              >
                Upgrade to Team →
              </button>
            </div>
          </div>
        )}
      </Section>
      {photosJob && <JobPhotosDialog job={photosJob} editable={false} onClose={() => setPhotosJob(null)} />}
    </div>
  );
}
